import dayjs from "dayjs";
import { GROUPS, GROUP_ORDER, getAccountDisplayName, getAccountName, ACCOUNT_ENVIRONMENTS } from "./accountGroups";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export const sumDaily = (daily) => daily.reduce((sum, d) => sum + d.cost, 0);

export const filterDailyInRange = (daily, startDate, endDate) =>
  daily.filter(d => d.date >= startDate && d.date <= endDate);

export const bucketByMonth = (daily) => {
  const totals = new Map();

  daily.forEach(({ date, cost }) => {
    const key = date.slice(0, 7); // YYYY-MM
    totals.set(key, (totals.get(key) || 0) + cost);
  });

  return Array.from(totals.entries())
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([key, value]) => {
      const monthIndex = Number(key.slice(5, 7)) - 1;
      const year = key.slice(0, 4);

      return {
        key,
        label: MONTH_LABELS[monthIndex],
        fullLabel: `${MONTH_NAMES[monthIndex]} ${year}`,
        value: parseFloat(value.toFixed(2))
      };
    });
};

// Fiscal year starts in April (month index 3). Quarter 0 = Apr-Jun, 1 = Jul-Sep,
// 2 = Oct-Dec, 3 = Jan-Mar (next FY, but still same calendar year's Jan-Mar).
export const getFiscalQuarterIndex = (date = dayjs()) => {
  const fyMonthIndex = (date.month() - 3 + 12) % 12;
  return Math.floor(fyMonthIndex / 3);
};

export const getFYQuartersToDate = (today = dayjs()) => {
  const year = today.year();
  const currentQuarterIndex = getFiscalQuarterIndex(today);
  const quarters = [];

  for (let q = 0; q <= currentQuarterIndex; q++) {
    const startMonth = (3 + q * 3) % 12;
    const monthsInQuarter = [0, 1, 2].map(o => (startMonth + o) % 12);

    quarters.push({
      quarterNumber: q + 1,
      monthsInQuarter,
      label: `Q${q + 1} (${MONTH_LABELS[monthsInQuarter[0]]}–${MONTH_LABELS[monthsInQuarter[2]]})`,
      isCurrent: q === currentQuarterIndex
    });
  }

  return { quarters, currentQuarterIndex, year };
};

export const getCurrentQuarterStartDate = (quarter, year) =>
  dayjs().year(year).month(quarter.monthsInQuarter[0]).date(1).format("YYYY-MM-DD");

// Natural full quarter range (Apr1-Jun30 etc.) regardless of "today" — future
// dates beyond "today" simply have no daily entries, so they total to zero
// and still show up on the axis (the in-progress-quarter UX we want).
export const getQuarterDateRange = (quarter, year) => {
  const startDate = dayjs().year(year).month(quarter.monthsInQuarter[0]).date(1).format("YYYY-MM-DD");
  const endDate = dayjs().year(year).month(quarter.monthsInQuarter[2]).endOf("month").format("YYYY-MM-DD");
  return { startDate, endDate };
};

// --- Per-account service-by-time trend (daily = [{ date, services: {name: cost} }]) ---

// Fixed categorical order (CVD-safe as a set) — color follows the entity, never
// its rank, so this ordering is assigned once from lifetime totals and reused
// unchanged across the Monthly/Weekly toggle.
const SERVICE_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300"];
const OTHER_COLOR = "#94a3b8";
const MAX_SERVICE_SLOTS = SERVICE_COLORS.length;

export const getTopServices = (daily, maxSlots = MAX_SERVICE_SLOTS) => {
  const totals = new Map();

  daily.forEach(({ services }) => {
    Object.entries(services).forEach(([name, cost]) => {
      totals.set(name, (totals.get(name) || 0) + cost);
    });
  });

  const sorted = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
  const top = sorted.slice(0, maxSlots).map(([name], i) => ({ name, color: SERVICE_COLORS[i] }));
  const hasOther = sorted.length > maxSlots;

  return { services: top, hasOther, otherColor: OTHER_COLOR };
};

const buildStackedRow = (key, label, fullLabel, serviceTotals, topServices, hasOther) => {
  const row = { key, label, fullLabel };
  let accountedFor = 0;

  topServices.forEach(({ name }) => {
    const value = serviceTotals[name] || 0;
    row[name] = parseFloat(value.toFixed(2));
    accountedFor += value;
  });

  const total = Object.values(serviceTotals).reduce((sum, v) => sum + v, 0);

  if (hasOther) {
    row.Other = parseFloat(Math.max(0, total - accountedFor).toFixed(2));
  }

  row.total = parseFloat(total.toFixed(2));
  return row;
};

export const bucketServiceTrendByMonth = (daily, topServices, hasOther) => {
  const monthlyTotals = new Map();

  daily.forEach(({ date, services }) => {
    const key = date.slice(0, 7);
    const bucket = monthlyTotals.get(key) || {};

    Object.entries(services).forEach(([name, cost]) => {
      bucket[name] = (bucket[name] || 0) + cost;
    });

    monthlyTotals.set(key, bucket);
  });

  return Array.from(monthlyTotals.keys())
    .sort()
    .map(key => {
      const monthIndex = Number(key.slice(5, 7)) - 1;
      const year = key.slice(0, 4);

      return buildStackedRow(
        key,
        MONTH_LABELS[monthIndex],
        `${MONTH_NAMES[monthIndex]} ${year}`,
        monthlyTotals.get(key),
        topServices,
        hasOther
      );
    });
};

export const bucketServiceTrendByWeek = (daily, topServices, hasOther) => {
  if (!daily.length) return [];

  const startOfYear = dayjs(daily[0].date).startOf("year");
  const weeklyTotals = new Map();

  daily.forEach(({ date, services }) => {
    const dayIndex = dayjs(date).diff(startOfYear, "day");
    const weekIndex = Math.floor(dayIndex / 7);
    const bucket = weeklyTotals.get(weekIndex) || {};

    Object.entries(services).forEach(([name, cost]) => {
      bucket[name] = (bucket[name] || 0) + cost;
    });

    weeklyTotals.set(weekIndex, bucket);
  });

  return Array.from(weeklyTotals.keys())
    .sort((a, b) => a - b)
    .map(weekIndex => {
      const weekStart = startOfYear.add(weekIndex * 7, "day");
      const weekEnd = weekStart.add(6, "day");

      return buildStackedRow(
        `w${weekIndex}`,
        weekStart.format("MMM D"),
        `${weekStart.format("MMM D")} – ${weekEnd.format("MMM D, YYYY")}`,
        weeklyTotals.get(weekIndex),
        topServices,
        hasOther
      );
    });
};

// --- Org-wide account-by-time trend (daily = [{ date, cost, accounts: {accountId: cost} }]) ---
// Stacks by every individual account (no top-N/"Other" cap — there are
// only 18 accounts total), colored two-dimensionally: hue = group (so an
// AMS account and a VMS account are never confusable), lightness = prod
// vs dev within that hue (prod darker, dev lighter) — Core Accounts has
// no prod/dev split (not a product), so it just uses declared order.

const GROUP_HUES = { ams: 150, vms: 170, oms: 90, re: 115, core: 195 };
const COLOR_SATURATION = 42;
const MIN_LIGHTNESS = 28;
const MAX_LIGHTNESS = 68;

// Computed once from the full YTD daily series (not per-view/per-quarter) so
// an account's color stays identical everywhere it's charted. An account is
// only included if it actually shows up in `daily` somewhere — this is what
// makes a team_member automatically see just their own accounts here too,
// same mechanism as the Accounts tree.
export const getAllAccounts = (daily) => {
  const seenAccountIds = new Set();

  daily.forEach(({ accounts }) => {
    Object.keys(accounts || {}).forEach(id => seenAccountIds.add(id));
  });

  const accounts = [];

  GROUP_ORDER.forEach(groupKey => {
    const visibleIds = GROUPS[groupKey].accountIds.filter(id => seenAccountIds.has(id));
    if (!visibleIds.length) return;

    const ordered = groupKey === "core"
      ? visibleIds
      : [...visibleIds].sort((a, b) => {
          const rankA = ACCOUNT_ENVIRONMENTS[a] === "prod" ? 0 : 1;
          const rankB = ACCOUNT_ENVIRONMENTS[b] === "prod" ? 0 : 1;
          return rankA - rankB;
        });

    const hue = GROUP_HUES[groupKey];
    const n = ordered.length;
    const step = n > 1 ? (MAX_LIGHTNESS - MIN_LIGHTNESS) / (n - 1) : 0;

    ordered.forEach((accountId, i) => {
      const lightness = n > 1 ? MIN_LIGHTNESS + step * i : (MIN_LIGHTNESS + MAX_LIGHTNESS) / 2;
      const name = getAccountName(accountId);

      accounts.push({
        key: accountId,
        name,
        title: getAccountDisplayName({ accountId, name }),
        groupKey,
        groupTitle: GROUPS[groupKey].title,
        color: `hsl(${hue}, ${COLOR_SATURATION}%, ${Math.round(lightness)}%)`
      });
    });
  });

  return { accounts, hasOther: false, otherColor: "#94a3b8" };
};

const buildAllAccountsStackedRow = (key, label, fullLabel, accountCosts, accounts) => {
  const row = { key, label, fullLabel };
  let total = 0;

  accounts.forEach(({ key: accountId, name }) => {
    const value = (accountCosts || {})[accountId] || 0;
    row[name] = parseFloat(value.toFixed(2));
    total += value;
  });

  row.total = parseFloat(total.toFixed(2));
  return row;
};

// Daily granularity is already the raw shape — one stacked row per existing
// entry, no aggregation needed.
export const bucketAllAccountsTrendByDay = (daily, accounts) =>
  daily.map(({ date, accounts: dayAccounts }) =>
    buildAllAccountsStackedRow(
      date,
      dayjs(date).format("MMM D"),
      dayjs(date).format("MMM D, YYYY"),
      dayAccounts,
      accounts
    )
  );

export const bucketAllAccountsTrendByWeek = (daily, accounts) => {
  if (!daily.length) return [];

  const startOfYear = dayjs(daily[0].date).startOf("year");
  const weeklyTotals = new Map();

  daily.forEach(({ date, accounts: dayAccounts }) => {
    const dayIndex = dayjs(date).diff(startOfYear, "day");
    const weekIndex = Math.floor(dayIndex / 7);
    const bucket = weeklyTotals.get(weekIndex) || {};

    Object.entries(dayAccounts || {}).forEach(([accountId, cost]) => {
      bucket[accountId] = (bucket[accountId] || 0) + cost;
    });

    weeklyTotals.set(weekIndex, bucket);
  });

  return Array.from(weeklyTotals.keys())
    .sort((a, b) => a - b)
    .map(weekIndex => {
      const weekStart = startOfYear.add(weekIndex * 7, "day");
      const weekEnd = weekStart.add(6, "day");

      return buildAllAccountsStackedRow(
        `w${weekIndex}`,
        weekStart.format("MMM D"),
        `${weekStart.format("MMM D")} – ${weekEnd.format("MMM D, YYYY")}`,
        weeklyTotals.get(weekIndex),
        accounts
      );
    });
};

export const bucketAllAccountsTrendByMonth = (daily, accounts) => {
  const monthlyTotals = new Map();

  daily.forEach(({ date, accounts: dayAccounts }) => {
    const key = date.slice(0, 7);
    const bucket = monthlyTotals.get(key) || {};

    Object.entries(dayAccounts || {}).forEach(([accountId, cost]) => {
      bucket[accountId] = (bucket[accountId] || 0) + cost;
    });

    monthlyTotals.set(key, bucket);
  });

  return Array.from(monthlyTotals.keys())
    .sort()
    .map(key => {
      const monthIndex = Number(key.slice(5, 7)) - 1;
      const year = key.slice(0, 4);

      return buildAllAccountsStackedRow(
        key,
        MONTH_LABELS[monthIndex],
        `${MONTH_NAMES[monthIndex]} ${year}`,
        monthlyTotals.get(key),
        accounts
      );
    });
};

// --- Same three, scoped to an explicit date range (used inside the Quarterly
// modal, where a chart covers just one fiscal quarter rather than the whole
// YTD span) ---

export const bucketAllAccountsTrendByDayInRange = (daily, startDate, endDate, accounts) =>
  bucketAllAccountsTrendByDay(filterDailyInRange(daily, startDate, endDate), accounts);

// Weeks that haven't started yet get total: null (a gap in the overlaid Total
// line) rather than 0 — a line dropping to zero would misread as "spend
// declined to zero" instead of "hasn't happened yet". Bars simply don't
// render for a row with no per-account values.
export const bucketAllAccountsTrendByWeekInRange = (daily, startDate, endDate, accounts, asOf = null) => {
  const rangeStart = dayjs(startDate);
  const rangeEnd = dayjs(endDate);
  const filtered = filterDailyInRange(daily, startDate, endDate);
  const totals = new Map();

  filtered.forEach(({ date, accounts: dayAccounts }) => {
    const dayIndex = dayjs(date).diff(rangeStart, "day");
    const weekIndex = Math.floor(dayIndex / 7);
    const bucket = totals.get(weekIndex) || {};

    Object.entries(dayAccounts || {}).forEach(([accountId, cost]) => {
      bucket[accountId] = (bucket[accountId] || 0) + cost;
    });

    totals.set(weekIndex, bucket);
  });

  const maxWeekIndex = Math.floor(rangeEnd.diff(rangeStart, "day") / 7);

  return Array.from({ length: maxWeekIndex + 1 }, (_, weekIndex) => {
    const weekStart = rangeStart.add(weekIndex * 7, "day");
    const weekEnd = weekStart.add(6, "day");
    const isFuture = asOf && weekStart.format("YYYY-MM-DD") > asOf;

    const row = buildAllAccountsStackedRow(
      `w${weekIndex}`,
      weekStart.format("MMM D"),
      `${weekStart.format("MMM D")} – ${weekEnd.format("MMM D, YYYY")}`,
      isFuture ? {} : (totals.get(weekIndex) || {}),
      accounts
    );

    if (isFuture) row.total = null;
    return row;
  });
};

// Months that haven't started yet get total: null — see bucketAllAccountsTrendByWeekInRange for why.
export const bucketAllAccountsTrendByQuarterMonths = (daily, monthsInQuarter, year, accounts, asOf = null) => {
  const monthlyTotals = new Map();

  daily.forEach(({ date, accounts: dayAccounts }) => {
    const key = date.slice(0, 7);
    const bucket = monthlyTotals.get(key) || {};

    Object.entries(dayAccounts || {}).forEach(([accountId, cost]) => {
      bucket[accountId] = (bucket[accountId] || 0) + cost;
    });

    monthlyTotals.set(key, bucket);
  });

  return monthsInQuarter.map((monthIndex) => {
    const key = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
    const monthStart = `${key}-01`;
    const isFuture = asOf && monthStart > asOf;

    const row = buildAllAccountsStackedRow(
      key,
      MONTH_LABELS[monthIndex],
      `${MONTH_NAMES[monthIndex]} ${year}`,
      isFuture ? {} : (monthlyTotals.get(key) || {}),
      accounts
    );

    if (isFuture) row.total = null;
    return row;
  });
};
